import { useEffect } from 'react';
import { useSticky } from './sticky';

/**
 * 화면 색(#381 · 10-22 정여진 「화면이 온통 흰색이라 눈이 아프다 · 다크모드나 색 변경」).
 *
 * 밝게(원래) / 부드럽게(흰 면을 옅은 회색으로) / 어둡게. 값은 `<html data-theme>` 하나 — 색은 CSS 가 바꾼다
 * (토큰 = globals.css · 팔레트 뒤집기 = tailwind.config.ts).
 * - 기억 = 계정(`user_pref` `ui.theme`, 다른 PC 에서도 같다) + 이 브라우저(localStorage) — 브라우저 쪽은 **첫 화면 깜빡임**을
 *   막는 용도다(index.html 이 React 전에 읽어 붙인다). 계정 값이 오면 그쪽이 이긴다.
 * - 🔴 기사앱(/driver)엔 안 붙인다 — 60대 기사 화면은 늘 밝게(index.html 도 같은 분기).
 */
export type Theme = 'light' | 'soft' | 'dark';
export const THEMES: readonly { id: Theme; label: string; icon: string }[] = [
  { id: 'light', label: '밝게', icon: '☀' },
  { id: 'soft', label: '부드럽게', icon: '◐' },
  { id: 'dark', label: '어둡게', icon: '☾' },
];
const LS_KEY = 'busin.theme';
const isTheme = (x: unknown): x is Theme => x === 'light' || x === 'soft' || x === 'dark';

function readLocal(): Theme {
  try {
    const v = localStorage.getItem(LS_KEY);
    return isTheme(v) ? v : 'light';
  } catch {
    return 'light';
  }
}

function applyTheme(t: Theme) {
  const el = document.documentElement;
  if (el.classList.contains('driver-app')) return;
  if (t === 'light') delete el.dataset.theme;
  else el.dataset.theme = t;
  try {
    localStorage.setItem(LS_KEY, t);
  } catch {
    /* 기억 못 해도 화면은 돈다 */
  }
}

/** 담당자 껍데기(App)에서 한 번 부른다 — 값이 바뀌면 바로 `<html>` 에 반영. */
export function useTheme(): [Theme, (t: Theme) => void] {
  const [theme, setTheme] = useSticky<Theme>('ui.theme', readLocal, { persist: true, valid: isTheme });
  useEffect(() => applyTheme(theme), [theme]);
  return [theme, setTheme];
}

/** 머리글·폰 서랍의 「화면 색」 단추 — 누를 때마다 밝게 → 부드럽게 → 어둡게. */
export function nextTheme(t: Theme): Theme {
  const i = THEMES.findIndex((x) => x.id === t);
  return THEMES[(i + 1) % THEMES.length]!.id;
}
export const themeOf = (t: Theme) => THEMES.find((x) => x.id === t) ?? THEMES[0]!;
